import { AnimatedSprite } from "pixi.js";
import {
  SpriteEffect,
  SpriteEffectProperties,
} from "@/client/sprite-animations";
import {
  create_color_matrix_filter,
  ResourceManager,
} from "@/client/resources";
import { SpriteColor } from "@/editor/blueprints/SpriteColor";
import { SpriteColorUpdate } from "@/editor/blueprints/SpriteColorUpdate";

type MainSpriteEffects = {
  base_props: SpriteEffectProperties;
  active_effects: SpriteEffect[];
  fade_in: SpriteEffect;
  fade_out: SpriteEffect;
  sprite: AnimatedSprite;
  color_matrix: ReturnType<typeof create_color_matrix_filter>;
  tileset_path: string;
  id: number;
};

type SpriteEffectMap = {
  [key: string]: MainSpriteEffects;
};

export class EffectsManager {
  private _active_animations: MainSpriteEffects[] = [];
  sprite_by_tileset_id_map: {
    [tileset_path: string]: {
      [id: string]: {
        effects: Set<string>;
        main_animation_sprite_key: string | null;
      };
    };
  } = {};
  sprite_effects_map: SpriteEffectMap = {};

  constructor(public sync_tiles: boolean = true) {}

  update_animations_for_animated_sprites(
    resource_manager: ResourceManager,
    tileset_path: string,
    id_in_tileset: number,
  ) {
    if (!this.sprite_by_tileset_id_map[tileset_path]?.[id_in_tileset]) {
      return;
    }
    const effects_for_gid =
      this.sprite_by_tileset_id_map[tileset_path][id_in_tileset];
    const graphics = resource_manager.get_graphics_by_id_and_tileset_path(
      id_in_tileset,
      tileset_path,
    );
    const is_animated = graphics.frame_objects.length > 0;
    for (const tile_key of effects_for_gid.effects.values()) {
      this.sprite_effects_map[tile_key].sprite.textures =
        graphics.frame_objects.length > 0
          ? graphics.frame_objects
          : graphics.textures;
      if (is_animated && !this.sync_tiles) {
        this.sprite_effects_map[tile_key].sprite.play();
      }
    }
    if (!this.sync_tiles) {
      return;
    }
    if (is_animated) {
      if (effects_for_gid.main_animation_sprite_key == null) {
        effects_for_gid.main_animation_sprite_key = effects_for_gid.effects
          .values()
          .next().value as string;
      }
      if (!this.sprite_effects_map[effects_for_gid.main_animation_sprite_key]) {
        // No main animation sprite key set
        return;
      }
      this.sprite_effects_map[
        effects_for_gid.main_animation_sprite_key
      ].sprite.loop = graphics.loop_animation !== false;
      this.sprite_effects_map[
        effects_for_gid.main_animation_sprite_key
      ].sprite.play();
    }

    if (!is_animated && effects_for_gid.main_animation_sprite_key != null) {
      effects_for_gid.main_animation_sprite_key = null;
    }
  }

  sync_sprite_animations() {
    if (!this.sync_tiles) {
      return;
    }
    for (const sprite_by_id_map of Object.values(
      this.sprite_by_tileset_id_map,
    )) {
      for (const sprite_map of Object.values(sprite_by_id_map)) {
        if (sprite_map.main_animation_sprite_key === null) {
          continue;
        }

        let main_sprite =
          this.sprite_effects_map[sprite_map.main_animation_sprite_key]?.sprite;
        // give role of main sprite to a different sprite if main sprite was removed
        if (!main_sprite) {
          sprite_map.main_animation_sprite_key =
            (sprite_map.effects.values().next().value as string) ?? null;
          main_sprite =
            this.sprite_effects_map[sprite_map.main_animation_sprite_key]
              ?.sprite;
          if (!main_sprite) {
            continue;
          }
          main_sprite.play();
        }
        const current_frame = main_sprite.currentFrame;
        for (const tile_key of sprite_map.effects.values()) {
          if (tile_key === sprite_map.main_animation_sprite_key) {
            continue;
          }
          this.sprite_effects_map[tile_key].sprite.currentFrame = current_frame;
        }
      }
    }
  }

  update_effects() {
    this._active_animations = this._active_animations.filter((effect) => {
      effect.active_effects.forEach((eff) =>
        eff.all_tweens.forEach((t) => t.update(window.performance.now())),
      );
      effect.sprite.position.x =
        effect.base_props.pos_x +
        effect.active_effects.reduce(
          (pos_x, eff) => pos_x + eff.add_props.pos_x,
          0,
        );
      effect.sprite.position.y =
        effect.base_props.pos_y +
        effect.active_effects.reduce(
          (pos_y, eff) => pos_y + eff.add_props.pos_y,
          0,
        );
      effect.sprite.rotation =
        effect.base_props.rotation +
        effect.active_effects.reduce(
          (rotation, eff) => rotation + eff.add_props.pos_x,
          0,
        );
      effect.sprite.alpha =
        effect.base_props.alpha +
        effect.active_effects.reduce(
          (alpha, eff) => alpha + eff.add_props.alpha,
          0,
        );
      effect.sprite.scale.x =
        effect.base_props.scale_x +
        effect.active_effects.reduce(
          (scale_x, eff) => scale_x + eff.add_props.scale_x,
          0,
        );
      effect.sprite.scale.y =
        effect.base_props.scale_y +
        effect.active_effects.reduce(
          (scale_y, eff) => scale_y + eff.add_props.scale_y,
          0,
        );
      if (
        effect.sprite.filters instanceof Array &&
        effect.sprite.filters.length > 0
      ) {
        const hue =
          effect.base_props.color.hue +
          effect.active_effects.reduce(
            (hue, eff) => hue + eff.add_props.color.hue,
            0,
          );
        const brightness =
          effect.base_props.color.brightness +
          effect.active_effects.reduce(
            (brightness, eff) => brightness + eff.add_props.color.brightness,
            0,
          );
        const tint_intensity =
          effect.base_props.color.tint_intensity +
          effect.active_effects.reduce(
            (tint_intensity, eff) =>
              tint_intensity + eff.add_props.color.tint_intensity,
            0,
          );
        this.update_sprite_color_matrix(
          effect,
          hue,
          brightness,
          tint_intensity,
          effect.base_props.color.tint_color,
        );
      }

      return effect.active_effects.some((eff) =>
        eff.all_tweens.some((t) => t.isPlaying()),
      );
    });
  }

  add_sprite_with_effects(
    sprite: AnimatedSprite,
    unique_key: string,
    tileset_path: string,
    id_in_tileset: number,
    fade_in_effect: SpriteEffect,
    fade_out_effect: SpriteEffect,
    sprite_color?: SpriteColor,
  ) {
    if (!this.sprite_by_tileset_id_map[tileset_path]) {
      this.sprite_by_tileset_id_map[tileset_path] = {};
    }
    if (!this.sprite_by_tileset_id_map[tileset_path][id_in_tileset]) {
      this.sprite_by_tileset_id_map[tileset_path][id_in_tileset] = {
        effects: new Set<string>(),
        main_animation_sprite_key: null,
      };
    }
    const is_animated = sprite.textures.length > 1;
    if (is_animated) {
      if (this.sync_tiles) {
        if (
          this.sprite_by_tileset_id_map[tileset_path][id_in_tileset]
            .main_animation_sprite_key == null
        ) {
          this.sprite_by_tileset_id_map[tileset_path][
            id_in_tileset
          ].main_animation_sprite_key = unique_key;
          sprite.play();
        }
      } else {
        sprite.play();
      }
    }

    this.sprite_effects_map[unique_key] = {
      base_props: {
        pos_x: sprite.x,
        pos_y: sprite.y,
        scale_x: sprite.scale.x,
        scale_y: sprite.scale.y,
        rotation: sprite.rotation,
        alpha: sprite.alpha,
        color: {
          brightness: sprite_color?.brightness ?? 1,
          tint_intensity: sprite_color?.tint_intensity ?? 0,
          tint_color: sprite_color?.tint_color ?? "#ffffff",
          hue: sprite_color?.hue ?? 0,
        },
      },
      fade_in: fade_in_effect,
      fade_out: fade_out_effect,
      active_effects: [fade_in_effect, fade_out_effect],
      sprite,
      color_matrix: window.medium.create_color_matrix_filter(),
      tileset_path,
      id: id_in_tileset,
    };

    const new_sprite_effect = this.sprite_effects_map[unique_key];
    this.update_sprite_color_matrix_from_base_props(new_sprite_effect);
    this._enable_color_matrix_filter(new_sprite_effect);
    this.sprite_by_tileset_id_map[tileset_path][id_in_tileset].effects.add(
      unique_key,
    );
    new_sprite_effect.fade_in.tween.start(window.performance.now());
    new_sprite_effect.fade_in.all_tweens[
      new_sprite_effect.fade_in.all_tweens.length - 1
    ].onComplete(() => {
      new_sprite_effect.active_effects =
        new_sprite_effect.active_effects.filter((e) => e !== fade_in_effect);
    });
    this._active_animations.push(new_sprite_effect);
  }

  private _enable_color_matrix_filter(sprite_effects: MainSpriteEffects) {
    if (
      sprite_effects.base_props.color.brightness !== 1 ||
      sprite_effects.base_props.color.hue !== 0 ||
      sprite_effects.base_props.color.tint_intensity !== 0 ||
      sprite_effects.base_props.color.tint_color !== "#ffffff"
    ) {
      sprite_effects.sprite.filters = [sprite_effects.color_matrix];
    }
  }

  get_sprite_effect_by_unique_key(
    tile_key: string,
  ): MainSpriteEffects | undefined {
    return this.sprite_effects_map[tile_key];
  }

  start_fade_out_animation(
    tile_key: string,
    on_complete: () => void = () => {},
  ) {
    const sprite_effect = this.sprite_effects_map[tile_key];
    this.remove_sprite_effect(tile_key);
    if (!sprite_effect) {
      console.error("Could not find sprite effect to remove?!", tile_key);
    } else {
      this._active_animations.push(sprite_effect);
      sprite_effect.fade_out.tween.start(window.performance.now());
      sprite_effect.fade_out.all_tweens[
        sprite_effect.fade_out.all_tweens.length - 1
      ].onComplete(() => {
        on_complete();
      });
    }
  }

  run_sprite_effect(unique_key: string, effect: SpriteEffect) {
    const sprite_effect = this.sprite_effects_map[unique_key];
    if (!sprite_effect) {
      console.error(
        "Could not find sprite effect to run effect?!",
        unique_key,
        this.sprite_effects_map,
      );
      return;
    }
    this.update_sprite_color_matrix_from_base_props(sprite_effect);
    sprite_effect.active_effects.push(effect);
    effect.tween.start(window.performance.now());
    effect.all_tweens[effect.all_tweens.length - 1].onComplete(() => {
      sprite_effect.active_effects = sprite_effect.active_effects.filter(
        (e) => e !== effect,
      );
    });
    this._active_animations.push(sprite_effect);
  }

  remove_sprite_effect(unique_key: string) {
    const sprite_effect = this.sprite_effects_map[unique_key];
    if (!sprite_effect) {
      console.error(
        "Could not find sprite effect to remove?!",
        unique_key,
        this.sprite_effects_map,
      );
    } else {
      if (
        this.sprite_by_tileset_id_map[sprite_effect.tileset_path]?.[
          sprite_effect.id
        ]
      ) {
        delete this.sprite_effects_map[unique_key];
        this.sprite_by_tileset_id_map[sprite_effect.tileset_path]?.[
          sprite_effect.id
        ].effects.delete(unique_key);
      } else {
        console.error(
          "Trying to remove a sprite that is not the main animation sprite",
        );
      }
    }
  }

  update_sprite_color_properties(
    unique_key: string,
    properties: SpriteColorUpdate,
  ) {
    const sprite_effect = this.sprite_effects_map[unique_key];
    if (!sprite_effect) {
      console.error(
        "Could not find sprite effect to update effect properties?!",
        unique_key,
        this.sprite_effects_map,
      );
      return;
    }
    sprite_effect.base_props.color.hue =
      properties.hue ?? sprite_effect.base_props.color.hue;
    sprite_effect.base_props.color.tint_color =
      properties.tint_color ?? sprite_effect.base_props.color.tint_color;
    sprite_effect.base_props.color.tint_intensity =
      properties.tint_intensity ??
      sprite_effect.base_props.color.tint_intensity;
    sprite_effect.base_props.color.brightness =
      properties.brightness ?? sprite_effect.base_props.color.brightness;
    this.update_sprite_color_matrix_from_base_props(sprite_effect);
    this._enable_color_matrix_filter(sprite_effect);
  }

  update_sprite_color_matrix_from_base_props(sprite_effect: MainSpriteEffects) {
    this.update_sprite_color_matrix(
      sprite_effect,
      sprite_effect.base_props.color.hue,
      sprite_effect.base_props.color.brightness,
      sprite_effect.base_props.color.tint_intensity,
      sprite_effect.base_props.color.tint_color,
    );
  }

  update_sprite_color_matrix(
    sprite_effect: MainSpriteEffects,
    hue: number,
    brightness: number,
    tint_intensity: number,
    tint_color: string,
  ) {
    sprite_effect.color_matrix.reset();
    sprite_effect.color_matrix.hue(hue, true);
    sprite_effect.color_matrix.brightness(brightness, true);
    sprite_effect.color_matrix.tint(
      this.interpolate_color(tint_color, "#ffffff", tint_intensity),
      true,
    );
  }

  interpolate_color(col1: string, col2: string, p: number) {
    const rgb1 = parseInt(col1.replace("#", ""), 16);
    const rgb2 = parseInt(col2.replace("#", ""), 16);

    const [r1, g1, b1] = this.toArray(rgb1);
    const [r2, g2, b2] = this.toArray(rgb2);

    const q = 1 - p;
    const rr = Math.round(r1 * p + r2 * q);
    const rg = Math.round(g1 * p + g2 * q);
    const rb = Math.round(b1 * p + b2 * q);

    return `#${Number((rr << 16) + (rg << 8) + rb).toString(16)}`;
  }

  toArray(rgb: number) {
    const r = rgb >> 16;
    const g = (rgb >> 8) % 256;
    const b = rgb % 256;

    return [r, g, b];
  }

  update_sprite(
    unique_key: string,
    sprite: AnimatedSprite,
    new_tileset_path: string,
    new_id: number,
  ) {
    const sprite_effect = this.sprite_effects_map[unique_key];
    if (!sprite_effect) {
      console.error(
        "Could not find sprite effect to update?!",
        unique_key,
        this.sprite_effects_map,
      );
      return;
    }
    const current_id_in_tileset = sprite_effect.id;
    const current_tileset_path = sprite_effect.tileset_path;
    if (
      current_tileset_path === new_tileset_path &&
      new_id === current_id_in_tileset
    ) {
      return;
    }
    if (!this.sprite_by_tileset_id_map[new_tileset_path]) {
      this.sprite_by_tileset_id_map[new_tileset_path] = {};
    }
    const new_id_map = this.sprite_by_tileset_id_map[new_tileset_path];
    if (!new_id_map[new_id]) {
      new_id_map[new_id] = {
        effects: new Set<string>(),
        main_animation_sprite_key: null,
      };
    }
    const current_id_map = this.sprite_by_tileset_id_map[current_tileset_path];
    if (current_id_map[current_id_in_tileset]) {
      current_id_map[current_id_in_tileset].effects.delete(unique_key);
      if (current_id_map[current_id_in_tileset].effects.size === 0) {
        delete current_id_map[current_id_in_tileset];
      } else {
        if (this.sync_tiles) {
          if (
            current_id_map[current_id_in_tileset].main_animation_sprite_key ===
            unique_key
          ) {
            const next_unique_key = current_id_map[
              current_id_in_tileset
            ].effects
              .values()
              .next().value;
            current_id_map[current_id_in_tileset].main_animation_sprite_key =
              next_unique_key;
            const next_sprite = this.sprite_effects_map[next_unique_key].sprite;
            next_sprite.play();
          }
        } else {
          sprite.play();
        }
      }
      sprite_effect.id = new_id;
      sprite_effect.tileset_path = new_tileset_path;
      sprite_effect.sprite = sprite;
      this._enable_color_matrix_filter(sprite_effect);
      const is_animated = sprite.textures.length > 1;
      new_id_map[new_id].effects.add(unique_key);
      if (is_animated) {
        sprite.play();
        if (this.sync_tiles) {
          if (new_id_map[new_id].main_animation_sprite_key == null) {
            new_id_map[new_id].main_animation_sprite_key = unique_key;
          }
        }
      }
    } else {
      console.error(
        "Trying to remove a sprite that is not the main animation sprite",
      );
    }
  }
}
